import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import {
  Bell, Plus, X, Pencil, XCircle, RotateCcw, AlertTriangle, CalendarX,
  Info, DollarSign, ParkingCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { StudioLayout } from "../../components/layout/StudioLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { cn } from "../../lib/utils";
import type { Doc, Id } from "convex/_generated/dataModel";

// ── Notice type config ────────────────────────────────────────────────────────

type NoticeType = "closure" | "cancellation" | "notice" | "fee_update" | "parking";

const NOTICE_TYPES: {
  value: NoticeType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badgeClass: string;
  bannerClass: string;
}[] = [
  {
    value: "closure",
    label: "Closure",
    icon: XCircle,
    badgeClass: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20",
    bannerClass: "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20",
  },
  {
    value: "cancellation",
    label: "Cancellation",
    icon: CalendarX,
    badgeClass: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20",
    bannerClass: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20",
  },
  {
    value: "notice",
    label: "Notice",
    icon: Info,
    badgeClass: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20",
    bannerClass: "border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20",
  },
  {
    value: "fee_update",
    label: "Fee Update",
    icon: DollarSign,
    badgeClass: "text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20",
    bannerClass: "border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20",
  },
  {
    value: "parking",
    label: "Parking",
    icon: ParkingCircle,
    badgeClass: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20",
    bannerClass: "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20",
  },
];

function getTypeConfig(type: NoticeType) {
  return NOTICE_TYPES.find((t) => t.value === type) ?? NOTICE_TYPES[2]!;
}

function formatDateRange(startDate?: string, endDate?: string) {
  if (!startDate && !endDate) return "Ongoing";
  if (startDate && !endDate) return `From ${startDate}`;
  if (!startDate && endDate) return `Until ${endDate}`;
  return `${startDate} – ${endDate}`;
}

// ── Notice form ───────────────────────────────────────────────────────────────

interface NoticeFormProps {
  venueId: Id<"venues">;
  existing?: Doc<"venueNotices">;
  onClose: () => void;
}

function NoticeForm({ venueId, existing, onClose }: NoticeFormProps) {
  const createNotice = useMutation(api.venueNotices.createNotice);
  const updateNotice = useMutation(api.venueNotices.updateNotice);
  const followerCount = useQuery(api.favorites.venueFollowerCount, {
    venueId,
  });

  const [type, setType] = useState<NoticeType>(existing?.type ?? "notice");
  const [title, setTitle] = useState(existing?.title ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [startDate, setStartDate] = useState(existing?.startDate ?? "");
  const [endDate, setEndDate] = useState(existing?.endDate ?? "");
  const [noEndDate, setNoEndDate] = useState(!existing?.endDate);
  // Only meaningful when posting: editing a notice does not re-notify, so the
  // control is hidden in that case rather than shown doing nothing.
  const [notifyFollowers, setNotifyFollowers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!title.trim()) { setError("Title is required."); return; }

    setSaving(true);
    setError(null);
    try {
      if (existing) {
        await updateNotice({
          noticeId: existing._id,
          type,
          title: title.trim(),
          body: body.trim() || undefined,
          startDate: startDate || undefined,
          endDate: noEndDate ? undefined : (endDate || undefined),
        });
      } else {
        await createNotice({
          venueId,
          type,
          title: title.trim(),
          body: body.trim() || undefined,
          startDate: startDate || undefined,
          endDate: noEndDate ? undefined : (endDate || undefined),
          notifySubscribers: notifyFollowers,
        });
      }
      onClose();
    } catch (e: any) {
      setError(e.message ?? "Failed to save notice.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{existing ? "Edit Notice" : "Post Notice"}</h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
      )}

      {/* Type selector */}
      <div>
        <label className="block text-sm font-medium text-foreground mb-2">Type</label>
        <div className="flex flex-wrap gap-2">
          {NOTICE_TYPES.map(({ value, label, icon: Icon, badgeClass }) => (
            <button
              key={value}
              type="button"
              onClick={() => setType(value)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-all",
                type === value
                  ? cn("border-current font-medium", badgeClass)
                  : "bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
              )}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Closed for the holiday weekend"
          maxLength={120}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1.5">Details <span className="text-muted-foreground font-normal">(optional)</span></label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Additional information for dancers..."
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Start Date <span className="text-muted-foreground font-normal">(optional)</span></label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">End Date</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={noEndDate}
            min={startDate || undefined}
          />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={noEndDate}
              onChange={(e) => setNoEndDate(e.target.checked)}
              className="rounded"
            />
            No end date
          </label>
        </div>
      </div>

      {!existing && (
        <div className="rounded-lg border border-border bg-secondary/30 p-3">
          <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={notifyFollowers}
              onChange={(e) => setNotifyFollowers(e.target.checked)}
              className="rounded mt-0.5"
            />
            <span>
              Email the people following this venue
              <span className="block text-xs text-muted-foreground mt-0.5">
                {followerCount === undefined
                  ? "Checking how many followers this venue has..."
                  : followerCount === 0
                    ? "Nobody is following this venue yet, so this won't send anything."
                    : followerCount === 1
                      ? "1 person follows this venue."
                      : `${followerCount} people follow this venue.`}
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : (existing ? "Save Changes" : "Post Notice")}
        </Button>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

// ── Notice card ───────────────────────────────────────────────────────────────

interface NoticeCardProps {
  notice: Doc<"venueNotices">;
  onEdit: () => void;
}

function NoticeCard({ notice, onEdit }: NoticeCardProps) {
  const deleteNotice = useMutation(api.venueNotices.deleteNotice);
  const updateNotice = useMutation(api.venueNotices.updateNotice);
  const { icon: Icon, label, badgeClass, bannerClass } = getTypeConfig(notice.type);

  return (
    <div className={cn("rounded-xl border p-4", bannerClass)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={cn("inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full", badgeClass)}>
              <Icon className="w-3 h-3" />
              {label}
            </span>
            {!notice.isActive && (
              <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">Inactive</span>
            )}
          </div>
          <h3 className="font-medium text-foreground">{notice.title}</h3>
          {notice.body && (
            <p className="text-sm text-foreground/70 mt-1 whitespace-pre-wrap">{notice.body}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1.5">
            {formatDateRange(notice.startDate, notice.endDate)}
          </p>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-background/50 transition-colors"
            title="Edit"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {notice.isActive ? (
            <button
              onClick={() => updateNotice({ noticeId: notice._id, isActive: false })}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-background/50 transition-colors"
              title="Deactivate"
            >
              <EyeOffIcon className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={() => updateNotice({ noticeId: notice._id, isActive: true })}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-background/50 transition-colors"
              title="Reactivate"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => deleteNotice({ noticeId: notice._id })}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-background/50 transition-colors"
            title="Delete"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Inline EyeOff icon (avoid import conflict)
function EyeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
    </svg>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function StudioNoticesPage() {
  const { user, isLoading } = useCurrentUser();
  const venues = useQuery(api.venues.getVenuesByOwner);
  const notices = useQuery(api.venueNotices.getMyNotices);

  const [selectedVenueId, setSelectedVenueId] = useState<Id<"venues"> | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Doc<"venueNotices"> | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  if (isLoading) {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">Loading...</div>
      </StudioLayout>
    );
  }

  if (!user || user.accountType !== "studio") {
    return (
      <StudioLayout>
        <div className="py-12 text-center text-muted-foreground">This page is only available to studio accounts.</div>
      </StudioLayout>
    );
  }

  const hasMultipleVenues = (venues?.length ?? 0) > 1;
  const activeVenueId = selectedVenueId ?? venues?.[0]?._id ?? null;

  const venueNotices = notices?.filter((n) => n.venueId === activeVenueId) ?? [];
  const activeNotices = venueNotices.filter((n) => n.isActive);
  const inactiveNotices = venueNotices.filter((n) => !n.isActive);

  return (
    <StudioLayout>
      {/* Venue selector (only if multiple venues) */}
      {hasMultipleVenues && venues && (
        <div className="flex flex-wrap gap-2 mb-6">
          {venues.map((venue) => (
            <button
              key={venue._id}
              onClick={() => setSelectedVenueId(venue._id)}
              className={cn(
                "px-3 py-1.5 rounded-lg text-sm border transition-all",
                activeVenueId === venue._id
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
              )}
            >
              {venue.name}
            </button>
          ))}
        </div>
      )}

      {/* Post notice button / form */}
      <div className="mb-6">
        {!showForm && !editingNotice && (
          <Button
            onClick={() => setShowForm(true)}
            disabled={!activeVenueId}
          >
            <Plus className="w-4 h-4" />
            Post Notice
          </Button>
        )}
        {showForm && activeVenueId && (
          <NoticeForm
            venueId={activeVenueId}
            onClose={() => setShowForm(false)}
          />
        )}
        {editingNotice && activeVenueId && (
          <NoticeForm
            venueId={activeVenueId}
            existing={editingNotice}
            onClose={() => setEditingNotice(null)}
          />
        )}
      </div>

      {/* Active notices */}
      <section className="mb-6">
        <h2 className="font-semibold text-lg text-foreground mb-3 flex items-center gap-2">
          <Bell className="w-5 h-5" />
          Active Notices
        </h2>

        {notices === undefined ? (
          <div className="space-y-3">
            {[1, 2].map((i) => <div key={i} className="h-20 bg-secondary/30 rounded-lg animate-pulse" />)}
          </div>
        ) : activeVenueId === null ? (
          <div className="text-center py-10 bg-card rounded-xl border border-border/50">
            <Bell className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-muted-foreground mb-2">No venues yet.</p>
            <p className="text-sm text-muted-foreground">Register a venue to start posting notices.</p>
          </div>
        ) : activeNotices.length === 0 ? (
          <div className="text-center py-10 bg-card rounded-xl border border-border/50">
            <Bell className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-muted-foreground mb-3">No active notices.</p>
            <p className="text-sm text-muted-foreground">Post a closure, cancellation, or advisory for your venue.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeNotices.map((notice) => (
              <NoticeCard
                key={notice._id}
                notice={notice}
                onEdit={() => {
                  setShowForm(false);
                  setEditingNotice(notice);
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Inactive notices */}
      {inactiveNotices.length > 0 && (
        <section>
          <button
            onClick={() => setShowInactive(!showInactive)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3"
          >
            {showInactive ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Inactive Notices ({inactiveNotices.length})
          </button>
          {showInactive && (
            <div className="space-y-3 opacity-60">
              {inactiveNotices.map((notice) => (
                <NoticeCard
                  key={notice._id}
                  notice={notice}
                  onEdit={() => {
                    setShowForm(false);
                    setEditingNotice(notice);
                  }}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </StudioLayout>
  );
}
